import * as THREE from "three";
import { vertexShader, fragmentShader } from "./glass-hero/shaders.js";

/** true: 자동 슬라이드 재생, false: 자동 재생 안 함 */
const AUTOPLAY_ENABLED = true;

function toAbsoluteImageUrl(url) {
    if (!url || typeof url !== "string") return url;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const base = window.location.origin || "";
    return url.startsWith("/") ? base + url : base + "/" + url;
}

const BEM = {
    block: "main-glass-hero",
    content: "main-glass-hero__content",
    title: "main-glass-hero__title",
    titleWord: "main-glass-hero__title-word",
    titleChar: "main-glass-hero__title-char",
    desc: "main-glass-hero__desc",
    descLine: "main-glass-hero__desc-line",
    descChar: "main-glass-hero__desc-char",
    pagination: "main-glass-hero__pagination",
    paginationSegment: "main-glass-hero__pagination-segment",
    paginationFill: "main-glass-hero__pagination-fill",
    swiper: "main-glass-hero__swiper",
    navPrev: "main-glass-hero__nav-prev",
    navNext: "main-glass-hero__nav-next",
};

function createCharacterElements(element) {
    if (element.querySelectorAll(`.${BEM.titleChar}`).length > 0) return;

    const html = element.innerHTML || "";
    const lineTexts = html
        .split(/<br\s*\/?>/i)
        .map((s) => s.replace(/<[^>]+>/g, "").trim());

    if (lineTexts.length === 0 || (lineTexts.length === 1 && !lineTexts[0])) {
        lineTexts.push("");
    }

    element.innerHTML = "";

    lineTexts.forEach((lineStr) => {
        const lineDiv = document.createElement("div");
        lineDiv.className = "main-glass-hero__title-line";

        const words = lineStr.split(/\s+/).filter(Boolean);

        words.forEach((word, index) => {
            const wordDiv = document.createElement("div");
            wordDiv.className = BEM.titleWord;

            [...word].forEach((char) => {
                const charDiv = document.createElement("div");
                charDiv.className = BEM.titleChar;
                charDiv.innerHTML = `<span>${char}</span>`;
                wordDiv.appendChild(charDiv);
            });

            lineDiv.appendChild(wordDiv);

            if (index < words.length - 1) {
                const space = document.createElement("div");
                space.className = BEM.titleWord;
                space.innerHTML = `<div class="${BEM.titleChar}"><span>&nbsp;</span></div>`;
                lineDiv.appendChild(space);
            }
        });

        element.appendChild(lineDiv);
    });
}

function createLineElements(container) {
    const p = container.querySelector("p");
    const source = p || container;
    const lines = [[]];

    const appendText = (text, format) => {
        const normalized = text.replace(/[^\S\r\n]+/g, " ").replace(/\r\n?/g, "\n");
        [...normalized].forEach((ch) => {
            if (ch === "\n") {
                lines.push([]);
                return;
            }
            lines[lines.length - 1].push({ ch, format });
        });
    };

    const collectCharacters = (node, format = null) => {
        if (node.nodeType === Node.TEXT_NODE) {
            appendText(node.textContent || "", format);
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const tag = node.tagName.toLowerCase();
        if (tag === "br") {
            lines.push([]);
            return;
        }

        const nextFormat = tag === "i" || tag === "em"
            ? { tag, className: node.className }
            : format;
        node.childNodes.forEach((child) => collectCharacters(child, nextFormat));
    };

    source.childNodes.forEach((node) => collectCharacters(node));

    const lineList = lines
        .map((line) => {
            while (line[0]?.ch === " ") line.shift();
            while (line[line.length - 1]?.ch === " ") line.pop();
            return line;
        })
        .filter((line) => line.length > 0);

    if (lineList.length === 0) lineList.push([]);

    container.innerHTML = "";

    lineList.forEach((characters) => {
        const lineDiv = document.createElement("div");
        lineDiv.className = BEM.descLine;
        const chars = characters.length > 0
            ? characters
            : [{ ch: "\u00a0", format: null }];
        chars.forEach(({ ch, format }) => {
            const wrap = document.createElement("span");
            wrap.className = BEM.descChar;
            const inner = document.createElement("span");
            const value = ch === " " ? "\u00a0" : ch;

            if (format) {
                const formatted = document.createElement(format.tag);
                formatted.className = format.className;
                formatted.textContent = value;
                inner.appendChild(formatted);
            } else {
                inner.textContent = value;
            }

            wrap.appendChild(inner);
            lineDiv.appendChild(wrap);
        });
        container.appendChild(lineDiv);
    });
}

function processTextElements(container) {
    const title = container.querySelector(`.${BEM.title}`);
    if (title) createCharacterElements(title);

    container.querySelectorAll(`.${BEM.desc}`).forEach((descEl) => {
        if (descEl.querySelector(`.${BEM.descChar}`)) return;
        createLineElements(descEl);
    });
}

/** 타이틀 글자 + 설명 글자 (opacity 애니용 타겟) */
function getHeroTextTargets(content) {
    if (!content) return [];
    const titleSpans = content.querySelectorAll(`.${BEM.titleChar} span`);
    const descSpans = content.querySelectorAll(`.${BEM.descChar} span`);
    return [...titleSpans, ...descSpans];
}

/**
 * 타이틀·설명 글자 랜덤 순서 opacity 페이드만 (이동 없음)
 */
function playHeroTextReveal(content, gsap, onComplete) {
    if (!content) {
        onComplete?.();
        return null;
    }
    const targets = getHeroTextTargets(content);
    if (!gsap) {
        targets.forEach((el) => {
            el.style.opacity = "1";
        });
        onComplete?.();
        return null;
    }
    if (targets.length === 0) {
        onComplete?.();
        return null;
    }
    gsap.killTweensOf(targets);
    gsap.set(targets, { opacity: 0, clearProps: "transform" });

    const n = targets.length;
    const staggerEach = Math.max(0.005, Math.min(0.018, 0.48 / Math.sqrt(n)));
    const fadeDuration = Math.min(0.48, Math.max(0.26, 0.18 + n * 0.0028));

    return gsap.to(targets, {
        opacity: 1,
        duration: fadeDuration,
        ease: "power2.out",
        stagger: {
            each: staggerEach,
            from: "random",
        },
        onComplete: () => onComplete?.(),
    });
}

function resetHeroTextOpacity(swiper, gsap) {
    if (!gsap || !swiper?.slides?.length) return;
    swiper.slides.forEach((slideEl) => {
        const c = slideEl.querySelector(`.${BEM.content}`);
        const t = getHeroTextTargets(c);
        if (t.length) gsap.set(t, { opacity: 0, clearProps: "transform" });
    });
}

function createSlideContent(slideData, contactUrl) {
    const content = document.createElement("div");
    content.className = BEM.content;
    content.style.opacity = "1";

    const titleHtml = (slideData.title || "").replace(/\n/g, "<br>");
    const descHtml = slideData.description || "";

    content.innerHTML = `
        <div class="main-glass-hero__inner" data-text-anim="fade">
            <div class="main-glass-hero__title-wrap">
                <h1 class="${BEM.title}">${titleHtml}</h1>
                <div class="${BEM.desc}"><p>${descHtml}</p></div>
            </div>
        </div>
    `;

    return content;
}

export class GlassHero {
    constructor() {
        this.root = null;
        this.canvas = null;
        this.slides = [];
        this.contactUrl = "#";
        this.currentIndex = 0;
        this.isTransitioning = false;
        this.slideTextures = [];
        this.slideMedia = [];
        this.shaderMaterial = null;
        this.renderer = null;
        this.autoplayDelay = 5000;
        this.progressRafId = null;
        this.progressStartAt = 0;
        this._autoplayTimer = null;
        this.contentTimeline = null;
        this.webglReady = false;
        this._resizeObserver = null;
        this._resizeRafId = null;
        this.swiper = null;
    }

    init() {
        this.root = document.querySelector(`.${BEM.block}`);
        if (!this.root) return;

        const dataSlides = this.root.getAttribute("data-slides");
        const contactUrlEl = this.root.getAttribute("data-contact-url");
        if (contactUrlEl) this.contactUrl = contactUrlEl;

        try {
            this.slides = JSON.parse(dataSlides || "[]");
        } catch (e) {
            console.warn("GlassHero: invalid data-slides", e);
            return;
        }

        if (this.slides.length === 0) return;

        this.canvas = this.root.querySelector(`.${BEM.block}__canvas`);
        if (!this.canvas) return;

        this.renderInitialContent();
        this.runInitialEffects();
        const onResize = () => {
            if (this._resizeRafId != null) cancelAnimationFrame(this._resizeRafId);
            this._resizeRafId = requestAnimationFrame(() => {
                this._resizeRafId = null;
                this.handleResize();
            });
        };
        window.addEventListener("resize", onResize);
        if (typeof ResizeObserver !== "undefined") {
            this._resizeObserver = new ResizeObserver(onResize);
            this._resizeObserver.observe(this.root);
        }
    }

    renderInitialContent() {
        const contentWrap = this.root.querySelector(
            `.${BEM.block}__content-wrap`,
        );
        const swiperEl = this.root.querySelector(`.${BEM.swiper}`);

        if (swiperEl) {
            // Swiper mode: process text for every slide
            const slides = swiperEl.querySelectorAll(".swiper-slide");
            slides.forEach((slideEl) => {
                const content = slideEl.querySelector(`.${BEM.content}`);
                if (content) {
                    content.style.opacity = "1";
                    processTextElements(content);
                    this.markSlideUpInview(content);
                }
            });
            // Set initial chars/lines hidden for GSAP entrance (first slide will animate in runInitialEffects)
            const gsap = window.gsap;
            if (gsap) {
                slides.forEach((slideEl) => {
                    const content = slideEl.querySelector(`.${BEM.content}`);
                    const targets = getHeroTextTargets(content);
                    if (targets.length)
                        gsap.set(targets, { opacity: 0, clearProps: "transform" });
                });
            }
            return;
        }

        // Legacy single-content mode
        let content = contentWrap?.querySelector(`.${BEM.content}`);
        if (!content && this.slides.length > 0) {
            const wrap =
                contentWrap ||
                (() => {
                    const w = document.createElement("div");
                    w.className = "main-glass-hero__content-wrap";
                    this.root.appendChild(w);
                    return w;
                })();
            content = createSlideContent(this.slides[0], this.contactUrl);
            wrap.replaceChildren(content);
        }
        if (content) {
            content.style.opacity = "1";
            processTextElements(content);
            this.markSlideUpInview(content);
            const gsap = window.gsap;
            if (gsap) {
                const targets = getHeroTextTargets(content);
                if (targets.length)
                    gsap.set(targets, { opacity: 0, clearProps: "transform" });
            }
        }
    }

    async runInitialEffects() {
        try {
            await this.initRenderer();
            this.webglReady = true;
            requestAnimationFrame(() => this.handleResize());
        } catch (e) {
            this.webglReady = false;
            console.warn("GlassHero: renderer init failed", e);
        }

        const swiperEl = this.root.querySelector(`.${BEM.swiper}`);
        if (swiperEl && typeof window.Swiper !== "undefined") {
            this.initSwiper(swiperEl);
            this.bindPagination();
            this.bindNav();
            this.root.classList.add("is-ready");
            const firstSlide = this.swiper.slides[this.swiper.activeIndex];
            this.animateActiveSlideContent(
                firstSlide?.querySelector(`.${BEM.content}`),
                () => this.startAutoplay(),
            );
            return;
        }

        this.bindPagination();
        this.bindNav();
        const content = this.root.querySelector(`.${BEM.content}`);
        const gsap = window.gsap;
        this.root.classList.add("is-ready");

        if (!content) {
            this.startAutoplay();
            return;
        }
        content.style.opacity = "1";
        const targets = getHeroTextTargets(content);
        if (!gsap) {
            targets.forEach((el) => {
                el.style.opacity = "1";
            });
            this.startAutoplay();
            return;
        }
        this.contentTimeline?.kill();
        this.contentTimeline = playHeroTextReveal(content, gsap, () =>
            this.startAutoplay(),
        );
    }

    initSwiper(swiperEl) {
        const self = this;
        this.swiper = new window.Swiper(swiperEl, {
            effect: "fade",
            fadeEffect: { crossFade: true },
            speed: 600,
            loop: this.slides.length > 1,
            allowTouchMove: this.slides.length > 1,
            autoplay: false,
            runCallbacksOnInit: false,
            on: {
                /** 인덱스가 바뀌는 즉시: 쉐이더용 “이전 슬라이드”는 sync 전에 보관 (currentIndex는 곧 새 값으로 바뀜) */
                slideChange() {
                    const sw = this;
                    const idx = sw.realIndex;
                    self._glassHeroFromIndex = self.currentIndex;
                    self.clearAutoplaySchedule();
                    self.syncPaginationIndex(idx);
                },
                slideChangeTransitionStart() {
                    const sw = this;
                    self.clearAutoplaySchedule();
                    self.syncPaginationIndex(sw.realIndex);
                    self.root
                        .querySelectorAll(`.${BEM.paginationFill}`)
                        .forEach((f) => {
                            f.style.width = "0%";
                        });
                    self.contentTimeline?.kill();
                    const G = window.gsap;
                    if (G) resetHeroTextOpacity(sw, G);
                    let fromIdx = self._glassHeroFromIndex;
                    if (
                        fromIdx === undefined &&
                        typeof sw.previousRealIndex === "number"
                    ) {
                        fromIdx = sw.previousRealIndex;
                    }
                    const toIdx = sw.realIndex;
                    if (
                        fromIdx !== undefined &&
                        toIdx !== undefined &&
                        fromIdx !== toIdx
                    ) {
                        self.runVisualTransition(fromIdx, toIdx);
                    }
                },
                slideChangeTransitionEnd() {
                    const sw = this;
                    const realIndex = sw.realIndex;
                    self.currentIndex = realIndex;
                    self.syncPaginationIndex(realIndex);
                    self.syncActiveMediaPlayback(realIndex);
                    const slideEl = sw.slides[sw.activeIndex];
                    const content = slideEl?.querySelector(`.${BEM.content}`);
                    requestAnimationFrame(() => {
                        self.animateActiveSlideContent(content, () =>
                            self.startAutoplay(),
                        );
                    });
                },
            },
        });
        if (this.slides.length <= 1) {
            this.root.classList.add("main-glass-hero--single");
        } else {
            this.syncPaginationIndex(this.swiper.realIndex ?? 0);
        }
    }

    /** 활성 슬라이드: 글자 단위 랜덤 순서 opacity 등장 */
    animateActiveSlideContent(content, onComplete) {
        this.contentTimeline?.kill();
        this.contentTimeline = playHeroTextReveal(
            content,
            window.gsap,
            onComplete,
        );
    }

    bindNav() {
        const prev = this.root.querySelector(`.${BEM.navPrev}`);
        const next = this.root.querySelector(`.${BEM.navNext}`);
        if (this.swiper) {
            prev?.addEventListener("click", () => {
                if (this.slides.length > 1) this.swiper.slidePrev();
            });
            next?.addEventListener("click", () => {
                if (this.slides.length > 1) this.swiper.slideNext();
            });
            return;
        }
        prev?.addEventListener("click", () => this.goTo(this.currentIndex - 1));
        next?.addEventListener("click", () => this.goTo(this.currentIndex + 1));
    }

    async initRenderer() {
        const scene = new THREE.Scene();
        const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: true,
        });

        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        const initRect = this.root.getBoundingClientRect();
        const initW = Math.max(1, Math.round(initRect.width));
        const initH = Math.max(1, Math.round(initRect.height));
        this.renderer.setSize(initW, initH);

        this.shaderMaterial = new THREE.ShaderMaterial({
            uniforms: {
                uTexture1: { value: null },
                uTexture2: { value: null },
                uProgress: { value: 0 },
                uResolution: {
                    value: new THREE.Vector2(initW, initH),
                },
                uTexture1Size: { value: new THREE.Vector2(1, 1) },
                uTexture2Size: { value: new THREE.Vector2(1, 1) },
            },
            vertexShader,
            fragmentShader,
        });

        scene.add(
            new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.shaderMaterial),
        );

        const loader = new THREE.TextureLoader();

        const fallbackTexture = (() => {
            const size = 2;
            const data = new Uint8Array(size * size * 4);
            for (let i = 0; i < data.length; i += 4) {
                data[i] = 6;
                data[i + 1] = 38;
                data[i + 2] = 58;
                data[i + 3] = 255;
            }
            const tex = new THREE.DataTexture(data, size, size);
            tex.needsUpdate = true;
            tex.minFilter = THREE.LinearFilter;
            tex.magFilter = THREE.LinearFilter;
            tex.userData = { size: new THREE.Vector2(size, size) };
            return tex;
        })();

        const firstTexture = await this.loadSlideTexture(
            this.slides[0],
            0,
            loader,
            fallbackTexture,
        );

        this.slideTextures[0] = firstTexture;
        this.shaderMaterial.uniforms.uTexture1.value = firstTexture;
        this.shaderMaterial.uniforms.uTexture2.value = firstTexture;
        this.shaderMaterial.uniforms.uTexture1Size.value =
            firstTexture.userData.size;
        this.shaderMaterial.uniforms.uTexture2Size.value =
            firstTexture.userData.size;

        const render = () => {
            if (!this.renderer) return;
            requestAnimationFrame(render);
            this.renderer.render(scene, camera);
        };
        render();

        this.syncActiveMediaPlayback(0);
        this.preloadRemainingTextures(loader, fallbackTexture);
    }

    loadTexture(loader, imageUrl, fallbackTexture) {
        return new Promise((resolve) => {
            if (!imageUrl) {
                resolve(fallbackTexture);
                return;
            }
            loader.load(
                imageUrl,
                (texture) => {
                    texture.minFilter = THREE.LinearFilter;
                    texture.magFilter = THREE.LinearFilter;
                    texture.userData = {
                        size: new THREE.Vector2(
                            texture.image?.width || 1,
                            texture.image?.height || 1,
                        ),
                    };
                    resolve(texture);
                },
                undefined,
                () => resolve(fallbackTexture),
            );
        });
    }

    async loadSlideTexture(slide, index, loader, fallbackTexture) {
        const imageUrl = toAbsoluteImageUrl(slide.image || slide.poster);
        const posterUrl = toAbsoluteImageUrl(slide.poster || slide.image);
        const videoUrl = toAbsoluteImageUrl(slide.video);

        if (!videoUrl) {
            const texture = await this.loadTexture(loader, imageUrl, fallbackTexture);
            this.slideMedia[index] = { video: null, texture, isVideo: false };
            return texture;
        }

        const baseTexture = await this.loadTexture(
            loader,
            posterUrl || imageUrl,
            fallbackTexture,
        );

        const video = this.createVideoElement(videoUrl, posterUrl, index === 0);
        this.slideMedia[index] = { video, texture: baseTexture, isVideo: true };
        this.promoteVideoTexture(index);

        return baseTexture;
    }

    createVideoElement(videoUrl, posterUrl, eager = false) {
        const video = document.createElement("video");
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.preload = eager ? "auto" : "metadata";
        video.crossOrigin = "anonymous";
        video.setAttribute("muted", "");
        video.setAttribute("playsinline", "");
        if (posterUrl) video.poster = posterUrl;
        video.src = videoUrl;
        video.load();
        return video;
    }

    promoteVideoTexture(index) {
        const media = this.slideMedia[index];
        const video = media?.video;
        if (!video) return;

        const applyVideoTexture = () => {
            if (!video.videoWidth || !video.videoHeight) return;

            const texture = new THREE.VideoTexture(video);
            texture.minFilter = THREE.LinearFilter;
            texture.magFilter = THREE.LinearFilter;
            texture.generateMipmaps = false;
            texture.userData = {
                size: new THREE.Vector2(video.videoWidth, video.videoHeight),
            };

            this.slideTextures[index] = texture;
            this.slideMedia[index] = { ...media, texture };

            if (
                this.shaderMaterial &&
                this.currentIndex === index &&
                !this.isTransitioning
            ) {
                this.shaderMaterial.uniforms.uTexture1.value = texture;
                this.shaderMaterial.uniforms.uTexture2.value = texture;
                this.shaderMaterial.uniforms.uTexture1Size.value =
                    texture.userData.size;
                this.shaderMaterial.uniforms.uTexture2Size.value =
                    texture.userData.size;
            }

            this.syncActiveMediaPlayback(this.currentIndex);
        };

        if (video.readyState >= 2) {
            applyVideoTexture();
            return;
        }

        const onReady = () => {
            cleanup();
            applyVideoTexture();
        };

        const onError = () => {
            cleanup();
        };

        const cleanup = () => {
            video.removeEventListener("loadeddata", onReady);
            video.removeEventListener("canplay", onReady);
            video.removeEventListener("error", onError);
        };

        video.addEventListener("loadeddata", onReady, { once: true });
        video.addEventListener("canplay", onReady, { once: true });
        video.addEventListener("error", onError, { once: true });
    }

    preloadRemainingTextures(loader, fallbackTexture) {
        this.slides.forEach(async (slide, index) => {
            if (this.slideTextures[index]) return;
            const texture = await this.loadSlideTexture(
                slide,
                index,
                loader,
                fallbackTexture,
            );
            this.slideTextures[index] = texture;
        });
    }

    syncActiveMediaPlayback(activeIndex) {
        this.slideMedia.forEach((media, index) => {
            const video = media?.video;
            if (!video) return;

            if (index === activeIndex) {
                const playPromise = video.play();
                if (playPromise?.catch) playPromise.catch(() => {});
            } else {
                video.pause();
            }
        });
    }

    primeTransitionMedia(fromIndex, toIndex) {
        [fromIndex, toIndex].forEach((index) => {
            const video = this.slideMedia[index]?.video;
            if (!video) return;
            const playPromise = video.play();
            if (playPromise?.catch) playPromise.catch(() => {});
        });
    }

    /** glass-hero 내부 블록: IO 없이 표시 상태만 맞춤 */
    markSlideUpInview(container) {
        if (!container) return;
        container.querySelectorAll('[data-text-anim="fade"]').forEach((el) => {
            requestAnimationFrame(() => {
                el.classList.add("is-inview");
            });
        });
    }

    getContentWrap() {
        return (
            this.root.querySelector(`.${BEM.block}__content-wrap`) ||
            this.root.querySelector(`.${BEM.content}`)?.parentElement
        );
    }

    /** Swiper/비-Swiper 공통: 대기 중인 자동 전환 타이머·진행 바 애니메이션 중단 */
    clearAutoplaySchedule() {
        if (this._autoplayTimer) {
            clearTimeout(this._autoplayTimer);
            this._autoplayTimer = null;
        }
        if (this.progressRafId) {
            cancelAnimationFrame(this.progressRafId);
            this.progressRafId = null;
        }
    }

    goTo(nextIndex) {
        if (this.isTransitioning || this.slides.length <= 1) return;

        nextIndex =
            ((nextIndex % this.slides.length) + this.slides.length) %
            this.slides.length;
        if (nextIndex === this.currentIndex) return;

        const wrap = this.getContentWrap();
        const currentContent = this.root.querySelector(`.${BEM.content}`);
        if (!wrap || !currentContent) return;

        this.isTransitioning = true;

        this.clearAutoplaySchedule();

        this.runVisualTransition(this.currentIndex, nextIndex);

        const gsap = window.gsap;
        const outTargets = getHeroTextTargets(currentContent);

        if (!gsap) {
            this.swapContentAndAnimateIn(nextIndex, wrap);
            return;
        }

        this.contentTimeline?.kill();

        if (!outTargets.length) {
            this.swapContentAndAnimateIn(nextIndex, wrap);
            return;
        }

        this.contentTimeline = gsap.to(outTargets, {
            opacity: 0,
            duration: 0.1,
            stagger: { amount: 0.22, from: "random" },
            ease: "power1.in",
            onComplete: () => this.swapContentAndAnimateIn(nextIndex, wrap),
        });
    }

    runVisualTransition(fromIndex, toIndex) {
        const gsap = window.gsap;
        if (!gsap) return;
        if (!this.webglReady) return;
        if (!this.shaderMaterial) return;

        const n = this.slides.length;
        const from =
            n > 0
                ? ((fromIndex % n) + n) % n
                : 0;
        const to =
            n > 0
                ? ((toIndex % n) + n) % n
                : 0;

        const texCurrent =
            this.slideTextures[from] || this.slideTextures[0];
        const texNext =
            this.slideTextures[to] ||
            texCurrent ||
            this.slideTextures[0];

        if (!texCurrent?.userData?.size || !texNext?.userData?.size) return;

        this.primeTransitionMedia(from, to);
        gsap.killTweensOf(this.shaderMaterial.uniforms.uProgress);

        this.shaderMaterial.uniforms.uTexture1.value = texCurrent;
        this.shaderMaterial.uniforms.uTexture2.value = texNext;
        this.shaderMaterial.uniforms.uTexture1Size.value =
            texCurrent.userData.size;
        this.shaderMaterial.uniforms.uTexture2Size.value =
            texNext.userData.size;

        gsap.fromTo(
            this.shaderMaterial.uniforms.uProgress,
            { value: 0 },
            {
                value: 1,
                duration: 2.5,
                ease: "power2.inOut",
                onComplete: () => {
                    this.shaderMaterial.uniforms.uProgress.value = 0;
                    this.shaderMaterial.uniforms.uTexture1.value = texNext;
                    this.shaderMaterial.uniforms.uTexture1Size.value =
                        texNext.userData.size;
                    this._glassHeroFromIndex = to;
                    this.syncActiveMediaPlayback(to);
                },
            },
        );
    }

    swapContentAndAnimateIn(nextIndex, wrap) {
        const newContent = createSlideContent(
            this.slides[nextIndex],
            this.contactUrl,
        );
        processTextElements(newContent);
        newContent.style.opacity = "1";

        wrap.replaceChildren(newContent);

        this.markSlideUpInview(newContent);
        this.currentIndex = nextIndex;
        this.syncPaginationIndex(nextIndex);
        this.syncActiveMediaPlayback(nextIndex);

        const gsap = window.gsap;
        this.contentTimeline?.kill();
        this.contentTimeline = playHeroTextReveal(newContent, gsap, () => {
            this.isTransitioning = false;
            this.startAutoplay();
        });
    }

    /**
     * 현재 슬라이드 인덱스에 맞춰 페이지네이션 활성화 + 모든 프로그래스 바 0%
     * (자동 재생 타이머/진행 애니는 startAutoplay에서만 시작)
     */
    syncPaginationIndex(index) {
        const n = this.slides.length;
        const idx =
            n > 0
                ? ((index % n) + n) % n
                : 0;
        this.currentIndex = idx;
        const segs = this.root.querySelectorAll(`.${BEM.paginationSegment}`);
        segs.forEach((seg, i) => {
            seg.classList.toggle("is-active", i === idx);
        });
        this.root.querySelectorAll(`.${BEM.paginationFill}`).forEach((f) => {
            f.style.width = "0%";
        });
        if (this.progressRafId) {
            cancelAnimationFrame(this.progressRafId);
            this.progressRafId = null;
        }
    }

    tickProgressForIndex(index) {
        if (this.progressRafId) cancelAnimationFrame(this.progressRafId);

        const n = this.slides.length;
        const idx =
            n > 0
                ? ((index % n) + n) % n
                : 0;
        const segs = this.root.querySelectorAll(`.${BEM.paginationSegment}`);
        const fill = segs[idx]?.querySelector(`.${BEM.paginationFill}`);
        if (!fill) return;

        const tick = () => {
            const elapsed = Date.now() - this.progressStartAt;
            const ratio = Math.min(1, elapsed / this.autoplayDelay);
            fill.style.width = `${ratio * 100}%`;

            if (ratio < 1) {
                this.progressRafId = requestAnimationFrame(tick);
            }
        };

        this.progressRafId = requestAnimationFrame(tick);
    }

    startAutoplay() {
        if (!AUTOPLAY_ENABLED || this.slides.length <= 1) return;

        this.clearAutoplaySchedule();

        const idx = this.swiper
            ? this.swiper.realIndex
            : this.currentIndex;
        this.syncPaginationIndex(idx);

        this.progressStartAt = Date.now();
        this.tickProgressForIndex(idx);

        this._autoplayTimer = setTimeout(() => {
            if (this.swiper) {
                this.swiper.slideNext();
            } else {
                this.goTo(this.currentIndex + 1);
            }
        }, this.autoplayDelay);
    }

    bindPagination() {
        this.root
            .querySelectorAll(`.${BEM.paginationSegment}`)
            .forEach((seg, i) => {
                seg.addEventListener("click", () => {
                    if (this.swiper && this.slides.length > 1) {
                        this.swiper.slideToLoop?.(i) ?? this.swiper.slideTo(i);
                    } else {
                        this.goTo(i);
                    }
                });
            });
    }

    handleResize() {
        if (!this.renderer || !this.canvas || !this.shaderMaterial) return;

        const rect = this.root.getBoundingClientRect();
        const w = Math.round(rect.width);
        const h = Math.round(rect.height);

        if (w <= 0 || h <= 0) return;

        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        this.renderer.setSize(w, h);
        this.shaderMaterial.uniforms.uResolution.value.set(w, h);
    }
}
